import type { ActivityItem } from "@salesboard/contracts";
import { useT } from "@/i18n";
import { cn } from "@/lib/utils";
import { formatAuditValue, formatDateTime } from "@/lib/format";
import { feedLabelKey, presentationFor } from "@/features/audit/registry";

const TONE_CLASSES = {
  neutral: "text-muted-foreground",
  positive: "text-success",
  negative: "text-destructive",
  warning: "text-warning",
} as const;

/**
 * The company-wide feed, as blocks rather than timeline rows.
 *
 * The per-record timelines keep the compact row — inside one opportunity's history the record
 * is a given, so a row is all a reader needs. A **cross-record** feed is a different problem:
 * every line carries a different actor, a different record and a different kind of event, and
 * a hundred one-line rows of that is a wall nobody scans. bd-admin solved the same problem
 * the same way, and the block is what gives the record it happened to somewhere to sit.
 *
 * Each block: the icon and the actor's sentence on the first line, the record and the time on
 * the second, and the old→new values beneath where the event carries them.
 *
 * The presentation — icon, wording, tone, whether values are shown — comes from the **same**
 * `presentationFor` registry the timelines use. A second presentation table for the same
 * events is how a feed and a record's own history start describing one change two ways.
 *
 * Consecutive-grouping is deliberately absent here: it collapses repetitions of one event on
 * one record, which is a property of a single record's history. Across the company the next
 * row is almost never the same event on the same record, and a grouped feed hides the very
 * interleaving a reader is looking for.
 */
export function ActivityBlocks({
  items,
  renderRecord,
}: {
  items: ActivityItem[];
  /** The record the event happened to, as something openable — supplied by the page. */
  renderRecord: (item: ActivityItem) => React.ReactNode;
}): React.JSX.Element {
  const t = useT();

  return (
    <ol className="space-y-2">
      {items.map((item) => {
        const presentation = presentationFor(item.eventType, item);
        const Icon = presentation.icon;

        return (
          <li key={item.id} className="bg-card rounded-md border p-3">
            <div className="flex items-start gap-3">
              <Icon
                className={cn("mt-0.5 h-4 w-4 shrink-0", TONE_CLASSES[presentation.tone ?? "neutral"])}
                aria-hidden
              />

              <div className="min-w-0 flex-1">
                {/* One sentence, naming what it happened to: "Hana Novotná created opportunity
                    «Data warehouse assessment»". The record used to sit on its own line below,
                    which left the first line saying "created this opportunity" about nothing in
                    particular. The separator is a catalogue entry: English reads straight on
                    from the name, Czech nominalises and needs a dash, because a Czech verb
                    would have to agree with a gender no audit row records. */}
                <p className="text-sm">
                  <span className="font-medium">{item.actorName}</span>
                  {t("audit.actorSeparator")}
                  {t(feedLabelKey(presentation.labelKey))}{" "}
                  {renderRecord(item)}
                </p>

                <p className="text-muted-foreground mt-1 text-xs">
                  <time dateTime={item.occurredAt}>{formatDateTime(item.occurredAt)}</time>
                </p>

                {presentation.showValues && (item.oldValue || item.newValue) && (
                  <p className="text-muted-foreground mt-1.5 text-xs">
                    {item.oldValue ? <span className="line-through">{formatAuditValue(item.oldValue)}</span> : <span>—</span>}
                    <span aria-hidden> → </span>
                    <span className="text-foreground">{formatAuditValue(item.newValue) ?? "—"}</span>
                  </p>
                )}
              </div>
            </div>
          </li>
        );
      })}
    </ol>
  );
}
